// Phase 10 /about — built-by-The-RLK-Group public page.
//
// Content strategy:
//   • SEO-tuned H1 that surfaces the "UK-built defence simulation
//     software" pairing.
//   • Prominent paragraph + outbound link to therlkgroup.com (brief
//     R4b asks for this specifically).
//   • Internal links to Use cases, Evidence, Contact (R3c).
//   • JSON-LD Organization with parentOrganization + BreadcrumbList.

import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import {
    buildBreadcrumbListSchema,
    buildDetailedOrganizationSchema,
} from "@/lib/schema";

export default function AboutPage() {
    return (
        <>
            <SeoHead
                path="/about"
                schemaJsonLd={[
                    buildDetailedOrganizationSchema(),
                    buildBreadcrumbListSchema({ path: "/about", label: "About" }),
                ]}
            />
            <div className="legal-doc" data-testid="about-page">
                <nav className="legal-doc__breadcrumbs" aria-label="Breadcrumbs">
                    <Link to="/" data-testid="about-breadcrumb-home">
                        Home
                    </Link>
                    <span aria-hidden>›</span>
                    <span>About</span>
                </nav>

                <header>
                    <h1 className="legal-doc__title" data-testid="about-h1">
                        Built by engineers who answer for the numbers.
                    </h1>
                    <p className="legal-doc__intro">
                        UAVSimNet is a counter-UAS simulation platform engineered in the
                        United Kingdom by The RLK Group — a defence-engineering
                        consultancy that has spent years inside programme reviews,
                        calibration sessions, and procurement scrutiny panels. We built
                        the platform we wanted to defend our own analysis with.
                    </p>
                </header>

                <section className="legal-doc__section">
                    <h2>From assumption to evidence.</h2>
                    <p>
                        Counter-UAS programmes carry two failure modes at the same time.
                        The threat moves faster than the procurement cycle. The evidence
                        produced to justify the buy decision rarely survives a credibility
                        review. UAVSimNet is the direct fix — a mission console engineers
                        can calibrate, operators can rehearse in, and programme offices
                        can defend in front of a scrutiny panel.
                    </p>
                    <p>
                        Every claim the platform produces carries a model registry entry,
                        a deterministic replay, and a published validity envelope. Nothing
                        leaves the console as a hand-drawn slide.
                    </p>
                </section>

                <section className="legal-doc__section">
                    <h2>Who builds it.</h2>
                    <p>
                        The RLK Group Ltd is a{" "}
                        <a
                            href="https://therlkgroup.com"
                            target="_blank"
                            rel="noopener"
                            data-testid="about-rlk-link"
                        >
                            UK-registered defence engineering consultancy
                            <ArrowUpRight
                                className="inline-block ml-1 -mt-0.5"
                                size={14}
                                aria-hidden
                            />
                        </a>
                        {" "}(Companies House number SC781391, registered in Scotland).
                        The group works across counter-UAS, airborne systems sustainment,
                        and digital decision-support for defence capability teams.
                    </p>
                    <p>
                        UAVSimNet is the group's flagship decision-support platform.
                        Engagements range from programme-office trade studies to bespoke
                        interceptor calibration sessions against live telemetry. Access
                        is by referral or through the{" "}
                        <Link to="/contact" data-testid="about-contact-link">
                            UAVSimNet access-request form
                        </Link>
                        .
                    </p>
                </section>

                <section className="legal-doc__section">
                    <h2>Where to read next.</h2>
                    <p>
                        If this is your first visit, three pages cover the operational
                        story end to end:
                    </p>
                    <ul>
                        <li>
                            <Link to="/use-cases" data-testid="about-link-use-cases">
                                Counter-UAS simulation use cases
                            </Link>{" "}
                            — who uses UAVSimNet and what they walk out with.
                        </li>
                        <li>
                            <Link to="/evidence" data-testid="about-link-evidence">
                                The UAVSimNet credibility framework
                            </Link>{" "}
                            — why the numbers survive a procurement review.
                        </li>
                        <li>
                            <Link to="/features" data-testid="about-link-features">
                                Feature tour
                            </Link>{" "}
                            — the operator console in detail.
                        </li>
                    </ul>
                </section>

                <section className="legal-doc__section">
                    <h2>Registered details</h2>
                    <ul>
                        <li>
                            <strong>Company:</strong> The RLK Group Ltd (SC781391)
                        </li>
                        <li>
                            <strong>Registered office:</strong> Unit 4 Strathclyde Business
                            Centre, Cambuslang, South Lanarkshire G72 7XR, United Kingdom
                        </li>
                        <li>
                            <strong>Group website:</strong>{" "}
                            <a
                                href="https://therlkgroup.com"
                                target="_blank"
                                rel="noopener"
                                data-testid="about-rlk-link-footer"
                            >
                                therlkgroup.com
                            </a>
                        </li>
                    </ul>
                </section>

                <div className="legal-doc__crosslinks">
                    <Link to="/contact">Request access</Link>
                    <Link to="/evidence">Credibility framework</Link>
                    <Link to="/faq">Frequently asked questions</Link>
                </div>
            </div>
        </>
    );
}
